import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { BillingPlanTier } from '@core/billing-types';
import { FormControlsOf, FormGroupOf } from '@core/common-legacy';
import { IconName } from '@core/user-interface-legacy';
import {
  AddFollowersActionData,
  AddNoteActionData,
  AddSourceTagsActionData,
  AddTagsActionData,
  AddTaskActionData,
  AddTeamMembersActionData,
  AssignActionData,
  AutomationActionType,
  AutomationConditionType,
  AutomationTriggerDataValue,
  AutomationTriggerType,
  CancelScheduledEventActionData,
  JobChangeStatusData,
  RequestEvaluationActionData,
  SendEmailActionData,
  ShareActionData,
  SubmitToJobBoardsActionData,
  TriggerDataSelectOption,
  UnassignActionData,
  UnshareActionData,
  AutomationConditionDataValue,
  TriggerConditionDataSelectOption,
} from '@core/workflow-automations-types';

// Automation form
export type AutomationForm = FormGroup<{
  trigger: FormGroup<AutomationTriggerForm>;
  conditions: FormArray<AutomationConditionForm>;
  actions: FormArray;
}>;

// Triggers
export type AutomationTriggerForm = {
  type: FormControl<AutomationTriggerType | null>;
  data: FormGroupOf<Record<string, AutomationTriggerDataValue>>;
};

export type TriggerOptionData = {
  label: string;
  name: string;
  options?: TriggerDataSelectOption[];
};

export type ActionOption = {
  type: AutomationActionType;
  label: string;
  icon: IconName;
  enableActionTooltip?: string;
  editActionTooltip?: string;
  recommended?: boolean;
  available: boolean;
  entitled: boolean;
  entitledFrom: BillingPlanTier | null;
};

// Conditions
export type AutomationConditionForm = FormGroup<{
  type: FormControl<AutomationConditionType | null>;
  data: FormGroupOf<Record<string, AutomationConditionDataValue>>;
}>;

export type TriggerConditionDataOptions = TriggerConditionDataSelectOption[];

export type ConditionOption = {
  type: AutomationConditionType;
  label: string;
  options: Array<{
    label: string;
    name: string;
    options?: TriggerConditionDataOptions;
  }>;
};

// Actions
export type AddTagsActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAddTags | AutomationActionType.JobAddTags>;
  data: FormGroupOf<AddTagsActionData>;
}>;
export type AddSourceTagsActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAddSourceTags>;
  data: FormGroupOf<AddSourceTagsActionData>;
}>;
export type AddTaskActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAddTask>;
  data: FormGroupOf<AddTaskActionData>;
}>;
export type AddFollowersActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAddFollowers>;
  data: FormGroupOf<AddFollowersActionData>;
}>;
export type AddNoteActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAddNote | AutomationActionType.JobAddNote>;
  data: FormGroupOf<AddNoteActionData>;
}>;
export type RequestEvaluationActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateEvaluationRequest>;
  data: FormGroupOf<RequestEvaluationActionData>;
}>;
export type AssignActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateAssignTo>;
  data: FormGroupOf<Pick<AssignActionData, 'offerIds' | 'talentPoolIds'>>;
}>;
export type UnassignActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateUnassignFromJob>;
  data: FormGroupOf<Pick<UnassignActionData, 'offerIds' | 'talentPoolIds' | 'includeSelf'>>;
}>;
export type SendEmailActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateSendEmail>;
  data: FormGroupOf<SendEmailActionData>;
}>;
export type CancelScheduledEventActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateCancelScheduledEvents>;
  data: FormGroupOf<CancelScheduledEventActionData>;
}>;
export type AddTeamMembersActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.JobAddTeamMembers>;
  data: FormGroupOf<AddTeamMembersActionData>;
}>;
export type JobChangeStatusActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.JobChangeStatus>;
  data: FormGroupOf<JobChangeStatusData>;
}>;
export type SendToTellentHrActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateSendToTellentHR>;
  data: FormGroupOf<null>;
}>;
export type SubmitToJobBoardsActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.JobSubmitToJobBoards>;
  data: FormGroup<FormControlsOf<SubmitToJobBoardsActionData>>;
}>;
export type ShareActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateShare>;
  data: FormGroupOf<ShareActionData>;
}>;
export type UnshareActionForm = FormGroup<{
  uuid?: FormControl<string>;
  type: FormControl<AutomationActionType.CandidateUnshare>;
  data: FormGroupOf<UnshareActionData>;
}>;

export type AutomationActionForm =
  | AddTagsActionForm
  | AddSourceTagsActionForm
  | AddTaskActionForm
  | AddFollowersActionForm
  | AddNoteActionForm
  | RequestEvaluationActionForm
  | AssignActionForm
  | UnassignActionForm
  | SendEmailActionForm
  | AddTeamMembersActionForm
  | JobChangeStatusActionForm
  | CancelScheduledEventActionForm
  | SendToTellentHrActionForm
  | SubmitToJobBoardsActionForm
  | ShareActionForm
  | UnshareActionForm;

export enum RecipientType {
  Candidate = 'candidate',
  Recruiter = 'recruiter',
  HiringManager = 'hiringManager',
}
